import { useState, useEffect } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { FaBars, FaSearch, FaUser, FaShoppingCart, FaHeart, FaArrowLeft } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import TopBanner from './TopBanner';
import NavOverlay from './NavOverlay';
import SearchOverlay from './SearchOverlay';
import { useCart } from './CartContext';
import MotionMenuIcon from './MotionMenuIcon';



export default function Navbar() {
  const { cartCount } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { scrollY } = useScroll();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const [userInfo, setUserInfo] = useState(null);



  useEffect(() => {
    const stored = localStorage.getItem('userInfo');
    if (stored) {
      try {
        setUserInfo(JSON.parse(stored));
      } catch (error) {
        console.error('Failed to parse userInfo from localStorage:', error);
        localStorage.removeItem('userInfo');
        setUserInfo(null);
      }
    } else {
      setUserInfo(null);
    }
  }, [location]);



  useMotionValueEvent(scrollY, "change", (latest) => {
    if (isHome) {
      setIsScrolled(latest > 36);
    }
  });



  useEffect(() => {
    if (!isHome) {
      setIsScrolled(true);
    } else {
      setIsScrolled(scrollY.get() > 36);
    }
  }, [isHome, scrollY]);



  const navbarClass = isHome
    ? (isScrolled 
        ? 'fixed top-0 left-0 right-0 bg-white shadow-sm text-gray-900' 
        : 'absolute top-[36px] left-0 right-0 bg-transparent text-white')
    : 'sticky top-0 left-0 right-0 bg-white shadow-sm text-gray-900';



  return (
    <>
      <TopBanner isHome={isHome} />
      <motion.header
        initial={isHome ? { y: -100, opacity: 0 } : { y: 0, opacity: 1 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className={`z-50 transition-colors duration-300 ${navbarClass}`}
      >
        <div className="container mx-auto px-6 py-4 relative">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0">
            {/* Main Row */}
            <div className="flex justify-between items-center w-full">
              {/* Left: Hamburger + Back Button */}
              <div className="flex items-center space-x-4 z-10 relative">
                <motion.button 
                  onClick={() => setIsNavOpen(true)} 
                  className="text-2xl focus:outline-none"
                  aria-label="Open Navigation Menu"
                  whileHover={{ color: '#f43f5e', scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <MotionMenuIcon isOpen={isNavOpen} />
                </motion.button>
                {!isHome && (
                  <motion.button
                    onClick={() => navigate(-1)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="hidden md:flex items-center space-x-2 text-sm font-semibold uppercase tracking-wider hover:text-rose-500 transition-colors"
                  >
                    <FaArrowLeft className="text-lg" />
                    <span>Back</span>
                  </motion.button>
                )}
              </div>


              <Link 
                to="/" 
                className="absolute left-[calc(50%-54px)] md:left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 md:-translate-y-[calc(54%-8px)] z-20"
              >
                <img 
                  src="/logo.avif" 
                  alt="SERA Logo" 
                  className="h-[120px] md:h-28 lg:h-32 xl:h-36 w-auto object-contain transition-opacity duration-300 hover:opacity-80" 
                />
              </Link>


              {/* Right: Icons */}
              <div className="flex items-center space-x-4 md:space-x-6 text-xl z-10 relative">

                <Link to="/shop" className="text-sm font-bold uppercase tracking-wider hidden md:block hover:text-rose-500 transition-colors">
                  Shop
                </Link>
                {userInfo && userInfo.role === 'admin' && (
                  <Link to="/admin" className="hidden md:block text-sm font-bold uppercase tracking-wider border border-current px-2 py-1 rounded hover:opacity-75 transition-opacity">
                    Admin
                  </Link>
                )}
                <motion.button 
                  onClick={() => setIsSearchOpen(true)}
                  aria-label="Open Search"
                  whileHover={{ color: '#f43f5e', scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaSearch />
                </motion.button>
                <Link to="/profile?tab=wishlist" className="p-2" aria-label="View Wishlist"><FaHeart className="hover:text-rose-500 transition-colors" /></Link>
                <Link to="/profile" className="p-2" aria-label="View Account"><FaUser className="hover:text-rose-500 transition-colors" /></Link>
                <Link to="/cart" className="relative p-2" aria-label="View Shopping Cart">
                  <FaShoppingCart className="hover:text-rose-500 transition-colors" />
                  <AnimatePresence>
                    {cartCount > 0 && (
                      <motion.span
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        className="absolute -top-2 -right-2 bg-rose-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 shadow-sm"
                        aria-label={`${cartCount} items in cart`}
                      >
                        {cartCount > 99 ? '99+' : cartCount}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Link>
              </div>
            </div>


            {/* Mobile Admin Button Row - Below Main Nav */}
            {userInfo && userInfo.role === 'admin' && (
              <div className="flex md:hidden justify-center w-full -mt-2">
                <Link to="/admin" className="text-xs font-bold uppercase tracking-wider border border-current px-3 py-1.5 rounded hover:opacity-75 transition-opacity">
                  Admin Panel
                </Link>
              </div>
            )}
          </div>
        </div>
      </motion.header>


      <NavOverlay isOpen={isNavOpen} onClose={() => setIsNavOpen(false)} />
      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
}
